import { useState } from 'react';
import { Facebook, Linkedin, MessageCircle, ChevronDown } from 'lucide-react';
import { useTranslation } from '../hooks';
import { contactInfo } from '../data';

const Footer = () => {
  const { t, language } = useTranslation();
  const [openServices, setOpenServices] = useState([]);

  const toggleService = (serviceId) => {
    setOpenServices(prev =>
      prev.includes(serviceId)
        ? prev.filter(id => id !== serviceId)
        : [...prev, serviceId]
    );
  };

  return (
    <footer className="bg-black text-white py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Logo & Description */}
          <div className="lg:col-span-2">
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-12 h-12 overflow-hidden rounded bg-white p-1">
                <img
                  src="/images/logo.jpg"
                  alt="M.A CONCEPT"
                  className="w-full h-full object-cover rounded"
                />
              </div>
              <div>
                <h3 className="text-2xl font-light tracking-wider">M.A CONCEPT</h3>
                <p className="text-gray-400 text-sm tracking-wide">{t.hero.tagline}</p>
              </div>
            </div>
            <p className="text-gray-400 leading-relaxed max-w-md mb-8">
              {t.footer.description}
            </p>
            <div className="flex space-x-4">
              <a
                href={contactInfo.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 hover:bg-blue-600 transition-colors cursor-pointer flex items-center justify-center rounded"
                aria-label="Facebook M.A CONCEPT"
              >
                <Facebook size={20} className="text-white" />
              </a>
              <a
                href={`https://wa.me/${contactInfo.whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 hover:bg-green-600 transition-colors cursor-pointer flex items-center justify-center rounded"
                aria-label="WhatsApp M.A CONCEPT"
              >
                <MessageCircle size={20} className="text-white" />
              </a>
              <a
                href={contactInfo.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 hover:bg-blue-700 transition-colors cursor-pointer flex items-center justify-center rounded"
                aria-label="LinkedIn M.A CONCEPT"
              >
                <Linkedin size={20} className="text-white" />
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-semibold mb-6 tracking-wide">{t.footer.services}</h4>
            <div className="space-y-2">
              {t.footer.footerServices.map((service) => (
                <div key={service.id} className="border-b border-gray-800">
                  <button
                    onClick={() => toggleService(service.id)}
                    className="w-full text-left py-3 flex items-center justify-between text-gray-400 hover:text-white transition-colors group"
                  >
                    <span className="text-sm">{service.title}</span>
                    <ChevronDown
                      size={16}
                      className={`transform transition-transform duration-300 ${
                        openServices.includes(service.id) ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      openServices.includes(service.id)
                        ? 'max-h-96 opacity-100 mb-3'
                        : 'max-h-0 opacity-0'
                    }`}
                  >
                    <p className="text-xs text-gray-500 leading-relaxed pb-2">
                      {service.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-semibold mb-6 tracking-wide">{t.footer.contactTitle}</h4>
            <ul className="space-y-4 text-gray-400">
              <li>
                <div className="font-medium text-white mb-1">{t.footer.phone}</div>
                {contactInfo.phones.map((phone, index) => (
                  <div key={index} className="text-sm">{phone}</div>
                ))}
              </li>
              <li>
                <div className="font-medium text-white mb-1">{t.footer.email}</div>
                <div className="text-sm">{contactInfo.email}</div>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-16 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="text-gray-400 text-sm mb-4 md:mb-0">
              {t.footer.legal.rights.replace('2025', new Date().getFullYear())}
            </div>
            <div className="flex space-x-8 text-gray-400 text-sm">
              <a href={language === 'en' ? '/legal-notice.html' : '/mentions-legales.html'} className="hover:text-white transition-colors">{t.footer.legal.legalNotice}</a>
              <a href={language === 'en' ? '/privacy-policy.html' : '/politique-confidentialite.html'} className="hover:text-white transition-colors">{t.footer.legal.privacy}</a>
              <a href={language === 'en' ? '/terms-of-use.html' : '/cgu.html'} className="hover:text-white transition-colors">{t.footer.legal.terms}</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
